const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function stampParts({ d, tz }: { readonly d: Date; readonly tz: string }) {
  const ps = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => ps.find((p) => p.type === t)?.value ?? "";
  return {
    day: `${get("year")}-${get("month")}-${get("day")}`,
    md: `${months[Number(get("month")) - 1]} ${Number(get("day"))}`,
    hhmm: `${get("hour").padStart(2, "0")}:${get("minute")}`.replace(
      /^24/,
      "00",
    ),
  };
}

type StampOpts = {
  readonly timeZone?: string;
  /** "Sep 13" / "today" — drops the clock time. */
  readonly dayOnly?: boolean;
  /** "16:40" — drops the date entirely, for the second half of a same-day span. */
  readonly timeOnly?: boolean;
};

/**
 * Timestamp in the design's idiom: "today 14:02" when the ISO instant shares a calendar day
 * with `now` in `timeZone`, else "Sep 13 18:20". Day-only variant: "Sep 13" / "today".
 * Time-only variant: "18:20", no date at all.
 */
export function formatStamp({
  iso,
  now,
  opts = {},
}: {
  readonly iso: string | null | undefined;
  readonly now: Date;
  readonly opts?: StampOpts;
}): string {
  if (!iso) return "—";

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return "—";

  const tz = opts.timeZone ?? "UTC";
  const p = stampParts({ d: date, tz });

  if (opts.timeOnly) return p.hhmm;

  const n = stampParts({ d: now, tz });

  if (p.day === n.day) return opts.dayOnly ? "today" : `today ${p.hhmm}`;

  return opts.dayOnly ? p.md : `${p.md} ${p.hhmm}`;
}
